import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface Props {
  /** Secret à afficher ; null ferme le dialogue. Il n'est conservé nulle part ailleurs. */
  secret: string | null;
  title: string;
  description: string;
  onClose: () => void;
}

/** Clé API ou secret de webhook renvoyé une seule fois par l'API : affichage + copie, jamais relu ensuite. */
export function SecretRevealDialog({ secret, title, description, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      toast.error('Copie impossible : sélectionnez le texte et copiez-le manuellement');
    }
  };

  const close = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={!!secret} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <p role="alert" className="text-sm font-medium text-amber-700">
          Cette valeur ne sera plus jamais affichée : copiez-la maintenant et conservez-la en lieu sûr.
        </p>
        <code data-testid="revealed-secret" className="block break-all rounded border bg-muted p-3 font-mono text-xs select-all">
          {secret}
        </code>
        <DialogFooter>
          <Button variant="outline" onClick={() => void copy()}>
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? 'Copié' : 'Copier'}
          </Button>
          <Button onClick={close}>J’ai copié, fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
